import { useVirtualizer } from "@tanstack/react-virtual"
import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * Scrollable, virtualised list for long collections (tool lists, provider
 * model catalogues). Rows may have different heights; they are measured.
 * Short lists (≤ `threshold`) render plainly so tests and print views see everything.
 */
export function VirtualList<T>({
  items,
  getKey,
  renderItem,
  estimateSize = 52,
  maxHeight = 420,
  threshold = 40,
  className,
  "aria-label": ariaLabel,
}: {
  items: T[]
  getKey: (item: T, index: number) => string | number
  renderItem: (item: T, index: number) => React.ReactNode
  estimateSize?: number
  maxHeight?: number | string
  threshold?: number
  className?: string
  "aria-label"?: string
}) {
  const parentRef = React.useRef<HTMLDivElement>(null)
  const virtual = items.length > threshold
  const virtualizer = useVirtualizer({
    count: virtual ? items.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 8,
  })

  if (!virtual)
    return (
      <div
        role="list"
        aria-label={ariaLabel}
        className={cn("overflow-y-auto", className)}
        style={{ maxHeight }}
      >
        {items.map((item, i) => (
          <div role="listitem" key={getKey(item, i)}>
            {renderItem(item, i)}
          </div>
        ))}
      </div>
    )

  return (
    <div
      ref={parentRef}
      role="list"
      aria-label={ariaLabel}
      className={cn("overflow-y-auto", className)}
      style={{ maxHeight }}
    >
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((v) => {
          const item = items[v.index]!
          return (
            <div
              key={getKey(item, v.index)}
              role="listitem"
              data-index={v.index}
              ref={virtualizer.measureElement}
              className="absolute top-0 left-0 w-full"
              style={{ transform: `translateY(${v.start}px)` }}
            >
              {renderItem(item, v.index)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
